import { createSelector } from "@reduxjs/toolkit";
import {
  GetMcbimSpacesInProjectApiArg,
  GetMcbimSpacesInProjectApiResponse,
  GetMcbimItemsInSpaceApiArg,
  GetMcbimItemsInSpaceApiResponse,
  PostMultiplacerDevicesApiArg,
  PostMultiplacerDevicesApiResponse,
  PostMultiplacerRoomsApiArg,
  PostMultiplacerRoomsApiResponse,
  openapi,
} from "../../../../../openapi";
import {
  calculateBestPolygonOverlap,
  getDeeplyNestedValue,
} from "@multiconsult-group/multiplacer-core";
import { IRootState } from "../../../../app/store";
import { IPixPath, IPixPolygon } from "@multiconsult-group/multiplacer-ui";
import { selectCompCodePath, selectMmiPath, selectProjectNo } from "../../../../globalSelectors";
import { selectActivePartialMcbimSpace } from "../../../../McBim/mcbimSelectors";
import { selectHttpSingleRoomFull } from "../../../../Room/httpRoomSelectors";
import {
  selectAdvancedCopySelectedTargetRoomNumber,
  selectAdvancedCopySelectedTargetRoomNumbers,
} from "../../../designSlice";
import { buildCeilingGridPixPathsFromLines } from "../../../../buildCeilingGridPixPathsFromLines";
import { convertMcBimSpaceToPixPolygon } from "../../../pixpolygonSelectors";
import { selectHttpDevicesInRoom } from "../../../devices/deviceSlice";
import { convertDevicesToPixPolygons } from "../../../devices/deviceUtils";
import { selectHttpMcBimItemsInSelectedRoom } from "../../../../RoomSideBar/singleRoomSelectors";
import { formatMcBimItemsToPixPolygons } from "../../../../utils";

export type AdvancedCopyDevicesTableRow = {
  roomNumber: string;
  roomName: string;
  building: string;
  floor: string;
  mmi: number | null;
  ceilingMmi: number | null;
  standardRoomNumber: string;
  hasExistingDevices: boolean;
  isCopiedInSession: boolean;
  similarityScore: number | null;
};

export const selectAdvancedCopyDevicesSourceRoomInfo = createSelector(
  [selectActivePartialMcbimSpace, selectHttpSingleRoomFull],
  (activeMcbimSpace, activeRoom) => ({
    roomName: activeMcbimSpace?.longName ?? "",
    roomNumber: activeMcbimSpace?.name ?? "",
    standardRoomNumber: activeRoom?.rfpMalRom ?? "",
  })
);

export const selectGetMatchingSpacesForAdvancedCopyArgs = createSelector(
  [selectProjectNo, selectActivePartialMcbimSpace],
  (projectNo, activeMcbimSpace): GetMcbimSpacesInProjectApiArg | null => {
    const longName = activeMcbimSpace?.longName;

    if (!projectNo || !longName) {
      return null;
    }

    return {
      projectNo,
      longName,
    };
  }
);

const EMPTY_SPACES: GetMcbimSpacesInProjectApiResponse = [];

export const selectCachedMatchingSpacesForAdvancedCopy = createSelector(
  [selectGetMatchingSpacesForAdvancedCopyArgs, (state: IRootState) => state],
  (args, state): GetMcbimSpacesInProjectApiResponse => {
    if (!args) {
      return EMPTY_SPACES;
    }

    return openapi.endpoints.getMcbimSpacesInProject.select(args)(state).data ?? EMPTY_SPACES;
  }
);

export const selectSelectedTargetMcbimSpaceForAdvancedCopy = createSelector(
  [selectCachedMatchingSpacesForAdvancedCopy, selectAdvancedCopySelectedTargetRoomNumber],
  (spaces, selectedTargetRoomNumber) => {
    if (!selectedTargetRoomNumber) {
      return null;
    }

    return spaces.find((space) => space.name === selectedTargetRoomNumber) ?? null;
  }
);

export const selectSelectedTargetRoomCountForAdvancedCopy = createSelector(
  [selectAdvancedCopySelectedTargetRoomNumbers],
  (selectedTargetRoomNumbers) => selectedTargetRoomNumbers.length
);

export const selectGetTargetMcbimItemsForAdvancedCopyArgs = createSelector(
  [selectProjectNo, selectSelectedTargetMcbimSpaceForAdvancedCopy],
  (projectNo, targetMcbimSpace): GetMcbimItemsInSpaceApiArg | null => {
    if (!projectNo || !targetMcbimSpace?._id) {
      return null;
    }

    return {
      projectNo,
      spaceId: String(targetMcbimSpace._id),
    };
  }
);

const EMPTY_MCBIM_ITEMS: GetMcbimItemsInSpaceApiResponse = [];

export const selectCachedTargetMcbimItemsForAdvancedCopy = createSelector(
  [selectGetTargetMcbimItemsForAdvancedCopyArgs, (state: IRootState) => state],
  (args, state): GetMcbimItemsInSpaceApiResponse => {
    if (!args) {
      return EMPTY_MCBIM_ITEMS;
    }

    return openapi.endpoints.getMcbimItemsInSpace.select(args)(state).data ?? EMPTY_MCBIM_ITEMS;
  }
);

export const selectAdvancedCopyDeviceTargetRoomNumbers = createSelector(
  [selectCachedMatchingSpacesForAdvancedCopy],
  (spaces): string[] => spaces.map((space) => space.name).filter(Boolean)
);

export const selectGetMatchingRoomsForAdvancedCopyArgs = createSelector(
  [selectProjectNo, selectAdvancedCopyDeviceTargetRoomNumbers],
  (projectNo, roomNumbers): PostMultiplacerRoomsApiArg | null => {
    if (!projectNo || roomNumbers.length === 0) {
      return null;
    }

    return {
      body: {
        projectNo,
        roomNumbers,
      },
    };
  }
);

export const selectGetMatchingDevicesForAdvancedCopyArgs = createSelector(
  [selectProjectNo, selectAdvancedCopyDeviceTargetRoomNumbers],
  (projectNo, roomNumbers): PostMultiplacerDevicesApiArg | null => {
    if (!projectNo || roomNumbers.length === 0) {
      return null;
    }

    return {
      body: {
        projectNo,
        roomNumbers,
      },
    };
  }
);

const EMPTY_ROOMS: PostMultiplacerRoomsApiResponse = {};
const EMPTY_TARGET_DEVICES: PostMultiplacerDevicesApiResponse = [];

export const selectCachedMatchingRoomsForAdvancedCopy = createSelector(
  [selectGetMatchingRoomsForAdvancedCopyArgs, (state: IRootState) => state],
  (args, state): PostMultiplacerRoomsApiResponse => {
    if (!args) {
      return EMPTY_ROOMS;
    }

    return openapi.endpoints.postMultiplacerRooms.select(args)(state).data ?? EMPTY_ROOMS;
  }
);

export const selectCachedMatchingDevicesForAdvancedCopy = createSelector(
  [selectGetMatchingDevicesForAdvancedCopyArgs, (state: IRootState) => state],
  (args, state): PostMultiplacerDevicesApiResponse => {
    if (!args) {
      return EMPTY_TARGET_DEVICES;
    }

    return openapi.endpoints.postMultiplacerDevices.select(args)(state).data ?? EMPTY_TARGET_DEVICES;
  }
);

export const selectSelectedTargetMcbimSpacesForAdvancedCopy = createSelector(
  [selectCachedMatchingSpacesForAdvancedCopy, selectAdvancedCopySelectedTargetRoomNumbers],
  (spaces, selectedTargetRoomNumbers) =>
    selectedTargetRoomNumbers
      .map((roomNumber) => spaces.find((space) => space.name === roomNumber) ?? null)
      .filter(
        (space): space is GetMcbimSpacesInProjectApiResponse[number] => space != null
      )
);

export const selectSelectedTargetRoomsForAdvancedCopy = createSelector(
  [selectCachedMatchingRoomsForAdvancedCopy, selectAdvancedCopySelectedTargetRoomNumbers],
  (rooms, selectedTargetRoomNumbers) =>
    selectedTargetRoomNumbers
      .map((roomNumber) => rooms[roomNumber] ?? null)
      .filter(
        (
          room
        ): room is NonNullable<PostMultiplacerRoomsApiResponse[string]> => room != null
      )
);

export const selectExistingDevicesInSelectedTargetRoomForAdvancedCopy = createSelector(
  [selectCachedMatchingDevicesForAdvancedCopy, selectAdvancedCopySelectedTargetRoomNumber],
  (devices, selectedTargetRoomNumber) => {
    if (!selectedTargetRoomNumber) {
      return EMPTY_TARGET_DEVICES;
    }

    return devices.filter((device) => device.roomNumber === selectedTargetRoomNumber);
  }
);

export const selectExistingDevicePixPolygonsInSelectedTargetRoomForAdvancedCopy = createSelector(
  [selectExistingDevicesInSelectedTargetRoomForAdvancedCopy],
  (devicesInRoom): IPixPolygon[] => {
    if (devicesInRoom.length === 0) {
      return [];
    }

    return convertDevicesToPixPolygons({
      devices: devicesInRoom,
      typeDevices: [],
      selectedDeviceId: null,
      hideDevices: false,
      showTrueSize: false,
      isMoveable: false,
    });
  }
);

export const selectNonPublishedDevicesInSelectedTargetRoomForAdvancedCopy = createSelector(
  [
    (state: IRootState) => state.design.nonPublischedDevicesFromAlgos,
    selectAdvancedCopySelectedTargetRoomNumber,
  ],
  (devices, selectedTargetRoomNumber) => {
    if (!selectedTargetRoomNumber) {
      return [];
    }

    return devices.filter((device) => device.roomNumber === selectedTargetRoomNumber);
  }
);

export const selectCopiedRoomNumbersForAdvancedCopy = createSelector(
  [(state: IRootState) => state.design.nonPublischedDevicesFromAlgos],
  (devices): string[] => Array.from(new Set(devices.map((device) => device.roomNumber).filter(Boolean)))
);

export const selectSelectedTargetRoomAlreadyCopiedForAdvancedCopy = createSelector(
  [selectCopiedRoomNumbersForAdvancedCopy, selectAdvancedCopySelectedTargetRoomNumber],
  (copiedRoomNumbers, selectedTargetRoomNumber) => {
    if (!selectedTargetRoomNumber) {
      return false;
    }

    return copiedRoomNumbers.includes(selectedTargetRoomNumber);
  }
);

export const selectNonPublishedDevicePixPolygonsInSelectedTargetRoomForAdvancedCopy =
  createSelector(
    [selectNonPublishedDevicesInSelectedTargetRoomForAdvancedCopy],
    (devicesInRoom): IPixPolygon[] => {
      if (devicesInRoom.length === 0) {
        return [];
      }

      return convertDevicesToPixPolygons({
        devices: devicesInRoom,
        typeDevices: [],
        selectedDeviceId: null,
        hideDevices: false,
        showTrueSize: false,
        isMoveable: false,
      });
    }
  );

export const selectSelectedTargetRoomForAdvancedCopy = createSelector(
  [selectCachedMatchingRoomsForAdvancedCopy, selectAdvancedCopySelectedTargetRoomNumber],
  (rooms, selectedTargetRoomNumber) => {
    if (!selectedTargetRoomNumber) {
      return null;
    }

    return rooms[selectedTargetRoomNumber] ?? null;
  }
);

export const selectAdvancedCopyDevicesTableRows = createSelector(
  [
    selectCachedMatchingSpacesForAdvancedCopy,
    selectCachedMatchingRoomsForAdvancedCopy,
    selectCachedMatchingDevicesForAdvancedCopy,
    selectCopiedRoomNumbersForAdvancedCopy,
    selectActivePartialMcbimSpace,
    selectMmiPath,
  ],
  (spaces, rooms, devices, copiedRoomNumbers, activeMcbimSpace, mmiPath): AdvancedCopyDevicesTableRow[] => {
    const sourcePolygon = activeMcbimSpace?.polygon;

    return spaces.map((space) => {
      const room = rooms[space.name];
      const hasExistingDevices = devices.some((device) => device.roomNumber === space.name);
      const isCopiedInSession = copiedRoomNumbers.includes(space.name);
      const rawMmi = mmiPath ? getDeeplyNestedValue(space, mmiPath) : null;
      const parsedMmi =
        rawMmi == null
          ? null
          : typeof rawMmi === "number"
            ? rawMmi
            : Number(String(rawMmi).trim());

      return {
        roomNumber: space.name,
        roomName: space.longName ?? "",
        building: space.building ?? "",
        floor: space.buildingStory?.name ?? "",
        mmi: Number.isFinite(parsedMmi) ? parsedMmi : null,
        ceilingMmi:
          typeof room?.extraIfcInfo?.ceilingMmi === "number"
            ? room.extraIfcInfo.ceilingMmi
            : typeof room?.extraIfcInfo?.ceilingMmi === "string"
              ? Number(String(room.extraIfcInfo.ceilingMmi).trim())
              : null,
        standardRoomNumber: room?.rfpMalRom ?? "",
        hasExistingDevices,
        isCopiedInSession,
        similarityScore: calculateBestPolygonOverlap(sourcePolygon, space.polygon),
      };
    });
  }
);

export const selectSelectedTargetTableRowForAdvancedCopy = createSelector(
  [selectAdvancedCopyDevicesTableRows, selectAdvancedCopySelectedTargetRoomNumber],
  (rows, selectedTargetRoomNumber) => {
    if (!selectedTargetRoomNumber) {
      return null;
    }

    return rows.find((row) => row.roomNumber === selectedTargetRoomNumber) ?? null;
  }
);

export const selectAdvancedCopySourcePixPolygon = createSelector(
  [selectActivePartialMcbimSpace],
  (activeMcbimSpace): IPixPolygon | undefined => {
    if (!activeMcbimSpace) {
      return undefined;
    }

    const polygon = convertMcBimSpaceToPixPolygon({
      mcBimSpace: activeMcbimSpace,
      dragSelectSpaces: false,
      showText: false,
    });

    if (!polygon) {
      return undefined;
    }

    return {
      ...polygon,
      text: {
        ...polygon.text,
        show: false,
      },
    };
  }
);

export const selectAdvancedCopySourcePixPolygons = createSelector(
  [selectAdvancedCopySourcePixPolygon],
  (polygon): IPixPolygon[] => {
    if (!polygon) {
      return [];
    }

    return [polygon];
  }
);

export const selectAdvancedCopySourceCeilingGridPixPaths = createSelector(
  [selectActivePartialMcbimSpace],
  (activeMcbimSpace): IPixPath[] => {
    if (!activeMcbimSpace) {
      return [];
    }

    return buildCeilingGridPixPathsFromLines([activeMcbimSpace]);
  }
);

export const selectAdvancedCopyTargetPixPolygon = createSelector(
  [selectSelectedTargetMcbimSpaceForAdvancedCopy],
  (targetMcbimSpace): IPixPolygon | undefined => {
    if (!targetMcbimSpace) {
      return undefined;
    }

    const polygon = convertMcBimSpaceToPixPolygon({
      mcBimSpace: targetMcbimSpace,
      dragSelectSpaces: false,
      showText: false,
    });

    if (!polygon) {
      return undefined;
    }

    return {
      ...polygon,
      text: {
        ...polygon.text,
        show: false,
      },
    };
  }
);

export const selectAdvancedCopyTargetPixPolygons = createSelector(
  [selectAdvancedCopyTargetPixPolygon],
  (polygon): IPixPolygon[] => {
    if (!polygon) {
      return [];
    }

    return [polygon];
  }
);

export const selectAdvancedCopyTargetCeilingGridPixPaths = createSelector(
  [selectSelectedTargetMcbimSpaceForAdvancedCopy],
  (targetMcbimSpace): IPixPath[] => {
    if (!targetMcbimSpace) {
      return [];
    }

    return buildCeilingGridPixPathsFromLines([targetMcbimSpace]);
  }
);

export const selectAdvancedCopyTargetFurniturePixPolygons = createSelector(
  [selectCachedTargetMcbimItemsForAdvancedCopy, selectCompCodePath],
  (mcbimItemsInRoom, compCodePath): IPixPolygon[] => {
    if (!compCodePath) {
      return [];
    }

    const furnitureItems = mcbimItemsInRoom.filter((item) => item.instanceClass === "IfcFurniture");
    if (furnitureItems.length === 0) {
      return [];
    }

    return formatMcBimItemsToPixPolygons({
      mcBimItems: furnitureItems,
      compCodePath,
      showDuctSegment: false,
    });
  }
);

export const selectAdvancedCopyTargetViewerPixPolygons = createSelector(
  [
    selectAdvancedCopyTargetPixPolygon,
    selectAdvancedCopyTargetFurniturePixPolygons,
    selectExistingDevicePixPolygonsInSelectedTargetRoomForAdvancedCopy,
    selectNonPublishedDevicePixPolygonsInSelectedTargetRoomForAdvancedCopy,
  ],
  (
    roomPolygon,
    furniturePolygons,
    existingDevicePolygons,
    nonPublishedDevicePolygons
  ): IPixPolygon[] => {
    if (!roomPolygon) {
      return [...furniturePolygons, ...existingDevicePolygons, ...nonPublishedDevicePolygons];
    }

    return [
      roomPolygon,
      ...furniturePolygons,
      ...existingDevicePolygons,
      ...nonPublishedDevicePolygons,
    ];
  }
);

export const selectAdvancedCopySourceDevicePixPolygons = createSelector(
  [selectHttpDevicesInRoom],
  (devicesInRoom): IPixPolygon[] => {
    if (devicesInRoom.length === 0) {
      return [];
    }

    return convertDevicesToPixPolygons({
      devices: devicesInRoom,
      typeDevices: [],
      selectedDeviceId: null,
      hideDevices: false,
      showTrueSize: false,
      isMoveable: false,
    });
  }
);

export const selectAdvancedCopySourceFurniturePixPolygons = createSelector(
  [selectHttpMcBimItemsInSelectedRoom, selectCompCodePath],
  (mcbimItemsInRoom, compCodePath): IPixPolygon[] => {
    if (!compCodePath) {
      return [];
    }

    const furnitureItems = mcbimItemsInRoom.filter((item) => item.instanceClass === "IfcFurniture");
    if (furnitureItems.length === 0) {
      return [];
    }

    return formatMcBimItemsToPixPolygons({
      mcBimItems: furnitureItems,
      compCodePath,
      showDuctSegment: false,
    });
  }
);

export const selectAdvancedCopySourceViewerPixPolygons = createSelector(
  [
    selectAdvancedCopySourcePixPolygon,
    selectAdvancedCopySourceFurniturePixPolygons,
    selectAdvancedCopySourceDevicePixPolygons,
  ],
  (roomPolygon, furniturePolygons, devicePolygons): IPixPolygon[] => {
    if (!roomPolygon) {
      return [...furniturePolygons, ...devicePolygons];
    }

    return [roomPolygon, ...furniturePolygons, ...devicePolygons];
  }
);

